import { getApiBase } from "../utils/apiBase";
import { useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueries, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";

import { api } from "../utils/api";
import { ApiResponse } from "../types/api";
import { FireSimulation, FrameArrays, ConfidenceArrays, UrbanAnimationFields } from "../types/FireSimulation";
import { useSimulation, SimulationParams } from "../contexts/SimulationContext";
import { StrategyContext } from "../contexts/StrategyContext";
import { decodeFramePack, FramePack } from "../utils/framePack";

/**
 * Fetch a list of all saved simulations for the current project.
 */

/**
 * Fetch simulations for an arbitrary project (used in project list risk column).
 */
export const useProjectSimulations = (projectId: string | number | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["simulations", projectId],
    queryFn: () =>
      api.request<ApiResponse<FireSimulation[]>>(
        `/api/projects/${projectId}/fire-simulations/`
      ),
    enabled: enabled && !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

/** Saved simulations for several projects at once, keyed by project id. */
export const useSimulationsForProjects = (
  projectIds: (number | string)[]
): { byProject: Record<string, FireSimulation[]>; isPending: boolean } => {
  const keys = useMemo(() => projectIds.map((id) => String(id)), [projectIds]);
  return useQueries({
    queries: keys.map((projectId) => ({
      queryKey: ["simulations", projectId],
      queryFn: () =>
        api.request<ApiResponse<FireSimulation[]>>(`/api/projects/${projectId}/fire-simulations/`),
      staleTime: 0,
      refetchOnWindowFocus: false as const,
    })),
    combine: (results) => {
      const byProject: Record<string, FireSimulation[]> = {};
      results.forEach((q, i) => {
        if (q.data?.data) byProject[keys[i]] = q.data.data;
      });
      return { byProject, isPending: results.some((q) => q.isPending) };
    },
  });
};

export const useSimulations = (poll: boolean = false) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery({
    queryKey: ["simulations", projectId],
    queryFn: () =>
      api.request<ApiResponse<FireSimulation[]>>( // update type when backend response defined
        `/api/projects/${projectId}/fire-simulations/`
      ),
    enabled: !!projectId,
    staleTime: 0,
    // Poll while explicitly requested (a tracked run) OR while any simulation
    // is in a short-lived transitional state: PREPARING/PENDING so every
    // viewer (not just the tab that started the run) sees the row progress
    // through preparation, and DELETING so its row clears once the background
    // cleanup finishes. Long-lived states (RUNNING) are not polled here —
    // each row's progress cell polls its own progress endpoint.
    refetchInterval: (query) => {
      if (poll) return 2000;
      try {
        const list = (query as any)?.state?.data?.data as FireSimulation[] | undefined;
        const anyTransitional = (list || []).some(
          (s) => s.status === 'DELETING' || s.status === 'PREPARING' || s.status === 'PENDING'
        );
        return anyTransitional ? 2000 : false;
      } catch {
        return false;
      }
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Delete a saved simulation and invalidate the list query.
 */
export const useDeleteSimulation = () => {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();

  return useMutation({
    mutationFn: async (simulationId: number | string) => {
      await api.request<void>(
        `/api/projects/${projectId}/fire-simulations/${simulationId}`,
        {
          method: "DELETE",
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulations", projectId] });
    },
  });
};

/**
 * Update a simulation's name and invalidate the list query.
 */
export const useUpdateSimulationName = () => {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();

  return useMutation({
    mutationFn: async ({ simulationId, name }: { simulationId: string; name: string }) => {
      const response = await api.request<ApiResponse<FireSimulation>>(
        `/api/projects/${projectId}/fire-simulations/${simulationId}`,
        {
          method: "PATCH",
          body: { name },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simulations", projectId] });
    },
  });
};

/**
 * Start a new simulation run (POST). Returns the simulation_id (uuid hex).
 */
export const useStartSimulation = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedStrategy } = useContext(StrategyContext);
  const { simulationParams } = useSimulation();

  return useMutation({
    // Callers may pass the freshly-built params directly. This avoids a race
    // where a view calls setSimulationParams() then immediately triggers the
    // mutation: React hasn't re-rendered yet, so the context value captured at
    // render time is still the previous run's params. An explicit override
    // always wins; otherwise fall back to the context value.
    mutationFn: async (overrideParams?: SimulationParams) => {
      const response = await api.request<ApiResponse<{ simulation_id: string }>>(
        `/api/projects/${projectId}/mitigationStrategies/${selectedStrategy?.id}/fireSimulation`,
        { method: "POST", body: overrideParams ?? simulationParams }
      );
      return response.data.simulation_id;
    },
  });
};

/**
 * Rerun a saved simulation: POST the params reconstructed from the saved
 * record against the strategy it was originally run under (falling back to
 * the currently selected strategy for legacy records without one).
 */
export const useRerunSimulation = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedStrategy } = useContext(StrategyContext);

  return useMutation({
    mutationFn: async ({
      strategyId,
      params,
    }: {
      strategyId?: string | number | null;
      params: SimulationParams;
    }) => {
      const response = await api.request<ApiResponse<{ simulation_id: string }>>(
        `/api/projects/${projectId}/mitigationStrategies/${strategyId ?? selectedStrategy?.id}/fireSimulation`,
        { method: "POST", body: params }
      );
      return response.data.simulation_id;
    },
  });
};

export type IgnitionProgressStatus = "QUEUED" | "SIMULATING" | "COMPLETE" | "ERROR";

export interface IgnitionProgress {
  ignition_idx: number;
  status: IgnitionProgressStatus;
  hours_simulated: number | null;
  total_hours: number | null;
  burnt_pixels: number | null;
  resolution_m: number | null;
  started_at: string | null;
  completed_at: string | null;
}

export type SimulationProgressStatus = "PREPARING" | "PENDING" | "RUNNING" | "SUCCESS" | "FAILURE" | "CANCELLED" | "COLLATING";

/** Machine keys for the backend's preparation stages, in order. */
export type SimulationPrepStage =
  | "queued"
  | "loading_layers"
  | "preparing_rasters"
  | "uploading_manifest"
  | "enqueueing_tasks"
  | "done";

export interface SimulationProgressData {
  progress: number;
  status: SimulationProgressStatus;
  ignitions: IgnitionProgress[];
  /** Present while (and after) the run prepares: what the prep task is doing. */
  prep_stage?: SimulationPrepStage | null;
  prep_detail?: string | null;
  /** ISO timestamp of the prep task's last progress report. */
  prep_heartbeat_at?: string | null;
  /** ISO timestamp the simulation record was created (kickoff time). */
  created_at?: string | null;
}

/**
 * Poll progress for a running simulation until it is completed/failed.
 * Does NOT fetch results. When completed, caller can load results via
 * useSimulationResults.
 */
export const useSimulationProgress = (simulationId: string | null) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery({
    queryKey: ["fireSimulationProgress", projectId, simulationId],
    queryFn: async () => {
      const response = await api.request<ApiResponse<SimulationProgressData>>(
        `/api/projects/${projectId}/fireSimulationProgress?simulation_id=${simulationId}`
      );
      return response.data;
    },
    enabled: !!projectId && !!simulationId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "SUCCESS" || status === "FAILURE" || status === "CANCELLED") return false;
      if (query.state.fetchStatus === "fetching") return false;
      return 3000;
    },
    refetchOnWindowFocus: false,
  });
};

/**
 * Load the processed results for a completed simulation (saved or freshly run).
 * Once fetched they are cached keyed by simulationId.
 */
/**
 * Results for a simulation of an explicit project — for pages not under a
 * /projects/:projectId route. Keyed by project too, so two projects' runs
 * can never share a cache entry.
 */
export const useSimulationResultsFor = (
  projectId: number | string | null | undefined,
  simulationId: string | null | undefined
) => {
  const idKey = projectId !== null && projectId !== undefined ? String(projectId) : undefined;
  return useQuery({
    queryKey: ["simulationResults", idKey, simulationId],
    queryFn: () =>
      api.request<
        ApiResponse<{ results: FireSimulation[]; kpis: { [key: string]: unknown } }>
      >(`/api/projects/${idKey}/fireSimulationResult?simulation_id=${simulationId}`),
    enabled: !!idKey && !!simulationId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useSimulationResults = (simulationId: string | null) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery({
    queryKey: ["simulationResults", simulationId],
    queryFn: () =>
      api.request<
        ApiResponse<{ results: FireSimulation[]; kpis: { [key: string]: any } }>
      >(
        `/api/projects/${projectId}/fireSimulationResult?simulation_id=${simulationId}`
      ),
    enabled: !!simulationId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};


/**
 * Convenience hook: returns results for the currently loaded simulation stored
 * in SimulationContext.
 */
export const useCurrentSimulationResults = () => {
  const { currentSavedSimId } = useSimulation();
  return useSimulationResults(currentSavedSimId);
};

/**
 * Check if a specific simulation is current (matches current mitigation strategy state).
 */
export const useSimulationIsCurrent = (simulationId: string | null) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery({
    queryKey: ["simulationIsCurrent", projectId, simulationId],
    queryFn: () =>
      api.request<ApiResponse<{ simulation_id: string; is_current: boolean }>>(
        `/api/projects/${projectId}/fire-simulations/${simulationId}/is-current`
      ),
    enabled: !!projectId && !!simulationId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes since this can change when mitigation strategy is modified
    refetchOnWindowFocus: false,
  });
};

export const useFireSimulationRasterImage = (
  simulationId: string | null,
  partition: string | null,
  metric: string | null,
  frame: number | null
) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery({
    queryKey: ["fireSimulationRasterImage", projectId, simulationId, partition, metric, frame],
    queryFn: () =>
      api.request<ApiResponse<{
        image_url: string;
        bounds: { west: number; south: number; east: number; north: number };
      }>>(
        `/api/projects/${projectId}/fire-simulation-raster-image?simulation_id=${simulationId}&partition=${partition}&metric=${metric}&frame=${frame}`
      ),
    enabled: !!projectId && !!simulationId && !!partition && !!metric && frame !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useFireSimulationPointQuery = (
  simulationId: string | null,
  partition: string | null,
  lat: number | null,
  lng: number | null,
  frame: number | null
) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery({
    queryKey: ["fireSimulationPointQuery", projectId, simulationId, partition, lat, lng, frame],
    queryFn: () =>
      api.request<ApiResponse<{
        timestamp: string | null;
        fire_size_ha: number | null;
        assets_impacted: number | null;
        fireline_intensity_kw_m: number | null;
        flame_height_m: number | null;
        rate_of_spread_km_h: number | null;
        arrival_time_hours: number | null;
        message?: string;
      }>>(
        `/api/projects/${projectId}/fire-simulation-point-query?simulation_id=${simulationId}&partition=${partition}&lat=${lat}&lng=${lng}&frame=${frame}`
      ),
    enabled: !!projectId && !!simulationId && !!partition && lat !== null && lng !== null && frame !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/** One burnt cell: [lng, lat, arrivalSeconds, peakFlameHeightM]. */
export type Fire3DCell = [number, number, number, number];

export interface Fire3DData {
  frame_count: number;
  bounds: { west: number; south: number; east: number; north: number };
  cells: Fire3DCell[];
}

/**
 * Fetch the whole fire's per-cell arrival frame + peak flame height once, for
 * driving the 3D fire animation entirely from memory (no per-frame queries).
 */
export const useFire3DData = (
  simulationId: string | null,
  partition: string | null,
  enabled: boolean = true
) => {
  const { projectId } = useParams<{ projectId: string }>();

  return useQuery({
    queryKey: ["fire3dData", projectId, simulationId, partition],
    queryFn: () =>
      api.request<ApiResponse<Fire3DData>>(
        `/api/projects/${projectId}/fire-simulation-3d-data?simulation_id=${simulationId}&partition=${partition}`
      ),
    enabled: !!projectId && !!simulationId && !!partition && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const downloadFireSimulationFrame = async (
  projectId: string,
  simulationId: string,
  partition: string,
  frame: number,
  projectName?: string,
  strategyName?: string,
  ignitionRegimeName?: string,
  simulationName?: string
): Promise<void> => {
  const url = `/api/projects/${projectId}/fire-simulation-frame-export?simulation_id=${simulationId}&partition=${partition}&frame=${frame}`;

  const response = await fetch(`${getApiBase()}${url}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download fire simulation frame');
  }

  const blob = await response.blob();

  // Build custom filename from provided names
  const safeProject = (projectName || 'Project').replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeStrategy = (strategyName || 'Strategy').replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeIgnition = (ignitionRegimeName || 'Ignition').replace(/[^a-zA-Z0-9_-]/g, '_');
  const safeSimulation = (simulationName || 'Simulation').replace(/[^a-zA-Z0-9_-]/g, '_');

  const filename = `${safeProject}_${safeStrategy}_${safeIgnition}_${safeSimulation}_frame${frame}.zip`;

  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

// ---------------------------------------------------------------------------
// Client-side animation: raw frame arrays
//
// These endpoints serve the compact rasters (arrival + at-ignition metrics, and
// the ensemble burn-probability stack) as a binary frame pack (see
// utils/framePack): cropped to the burned window, gzip'd on the wire, viewed in
// place as typed arrays. The animation layer reconstructs each frame itself on
// a canvas, so frames are computed locally rather than fetched per-frame.
// ---------------------------------------------------------------------------

/** Query-string flag selecting the binary frame pack over the legacy JSON. */
export const FRAME_PACK_PARAM = "format=fsp1";

const URBAN_KEYS = [
  'urban_asset_ignition_s',
  'urban_asset_burnout_s',
  'urban_asset_ignition_mode',
  'urban_asset_radiant_exposure',
  'urban_asset_ids',
  'urban_ignition_edges',
  'urban_node_positions',
  'urban_ignition_mode_counts',
  // Tree / shrub crown nodes, keyed by Crown id (not Asset id).
  'urban_crown_ignition_s',
  'urban_crown_burnout_s',
  'urban_crown_ignition_mode',
] as const;

/** Pick the urban fields out of a raw response, or undefined if none present. */
export function pickUrban(r: UrbanAnimationFields): UrbanAnimationFields | undefined {
  const urban: UrbanAnimationFields = {};
  let has = false;
  for (const k of URBAN_KEYS) {
    if (r[k] !== undefined) {
      (urban as Record<string, unknown>)[k] = r[k];
      has = true;
    }
  }
  return has ? urban : undefined;
}

/** A partition's / member's field from its frame pack. */
export function frameArraysFromPack({ meta, arrays }: FramePack): FrameArrays {
  return {
    shape: meta.shape,
    bounds: meta.bounds,
    domainBounds: meta.domain_bounds,
    frameTimesSeconds: meta.frame_times_seconds,
    arrival: arrays.arrival as Float32Array,
    rosAtIgnition: arrays.ros_at_ignition as Float32Array,
    intensityAtIgnition: arrays.intensity_at_ignition as Float32Array,
    flameHeightAtIgnition: arrays.flame_height_at_ignition as Float32Array,
    maxRos: meta.max_ros ?? 0,
    maxIntensity: meta.max_intensity ?? 0,
    maxFlameHeight: meta.max_flame_height ?? 0,
    maxHour: meta.max_hour ?? 0,
    member: meta.member,
    urban: pickUrban(meta),
  };
}

/** The across-member burn-probability stack from its frame pack. */
export function confidenceArraysFromPack({ meta, arrays }: FramePack): ConfidenceArrays {
  return {
    shape: meta.shape,
    bounds: meta.bounds,
    domainBounds: meta.domain_bounds,
    frameTimesSeconds: meta.frame_times_seconds,
    members: meta.members ?? 0,
    frameCount: meta.frame_count ?? 0,
    burnProb: arrays.burn_prob as Uint8Array,
  };
}

/** GET an authed animation endpoint as a decoded frame pack. */
export const fetchFramePack = async (url: string, signal?: AbortSignal): Promise<FramePack> =>
  decodeFramePack(
    await api.request<ArrayBuffer>(
      `${url}${url.includes("?") ? "&" : "?"}${FRAME_PACK_PARAM}`,
      { signal, responseType: "arrayBuffer" }
    )
  );

/** Compact arrays for one partition (single run, or a clicked ignition). */
export const useFireSimulationFrameArrays = (
  simulationId: string | null,
  partition: string | null
) => {
  const { projectId } = useParams<{ projectId: string }>();
  return useQuery({
    queryKey: ["fireFrameArrays", projectId, simulationId, partition],
    // Consuming the abort signal lets react-query cancel the request the
    // moment the observer moves to another partition (e.g. scrubbing the
    // scenario slider with an ignition selected), so abandoned fetches don't
    // hold browser connections ahead of the one actually on screen.
    queryFn: async ({ signal }) =>
      frameArraysFromPack(
        await fetchFramePack(
          `/api/projects/${projectId}/fire-simulation-frame-arrays?simulation_id=${simulationId}&partition=${partition}`,
          signal
        )
      ),
    enabled: !!projectId && !!simulationId && !!partition,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // A 404 means the partition has no compact frame arrays (a legacy
    // simulation that predates them) — retrying can't fix that, and the
    // consumer wants to react to it immediately.
    retry: (failureCount, error) =>
      (error as { status?: number })?.status === 404 ? false : failureCount < 3,
    // Keep the previous partition rendered while the next one loads (e.g.
    // scrubbing the ensemble scenario slider with an ignition selected).
    placeholderData: keepPreviousData,
  });
};

/**
 * True when a frame-arrays query failed because the simulation predates
 * compact frame arrays (backend 404: "... has no compact frame arrays").
 * Such simulations cannot be played back and must be rerun.
 */
export const isMissingFrameArraysError = (err: unknown): boolean => {
  const e = err as { status?: number; data?: { data?: { error?: string }; error?: string } } | null;
  if (!e || e.status !== 404) return false;
  const message = e.data?.data?.error ?? e.data?.error ?? '';
  return typeof message === 'string' && message.includes('no compact frame arrays');
};

const fetchMemberArrays = async (
  projectId: string,
  simulationId: string,
  memberIdx: number,
  signal?: AbortSignal
): Promise<FrameArrays> => ({
  ...frameArraysFromPack(
    await fetchFramePack(
      `/api/projects/${projectId}/fire-simulation-member-arrays?simulation_id=${simulationId}&member=${memberIdx}`,
      signal
    )
  ),
  member: memberIdx,
});

const memberArraysQueryKey = (
  projectId: string | undefined,
  simulationId: string | null,
  memberIdx: number | null
) => ["fireMemberArrays", projectId, simulationId, memberIdx];

/** One ensemble member's combined field, computed on demand server-side. */
export const useFireSimulationMemberArrays = (
  simulationId: string | null,
  memberIdx: number | null
) => {
  const { projectId } = useParams<{ projectId: string }>();
  return useQuery({
    queryKey: memberArraysQueryKey(projectId, simulationId, memberIdx),
    // Consume the abort signal: when the slider scrubs on past this member,
    // its request is cancelled immediately instead of queueing ahead of the
    // member the user actually stopped on. (The background prefetch uses a
    // non-cancelling fetcher, so cache warming is unaffected.)
    queryFn: ({ signal }) => fetchMemberArrays(projectId!, simulationId!, memberIdx!, signal),
    enabled: !!projectId && !!simulationId && memberIdx !== null && memberIdx >= 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Keep the previous member's animation on the canvas while the next
    // member's arrays load, so scrubbing the scenario slider never blanks.
    placeholderData: keepPreviousData,
  });
};

/**
 * Per-member cache status (indexed by member idx): true where that member's
 * arrays are already in the client cache, i.e. the scenario slider can show
 * it instantly without a fetch. Observes the cache reactively (enabled:
 * false, so it never fetches itself; the background prefetch and on-demand
 * queries do the loading).
 */
export const useLoadedEnsembleMembers = (
  simulationId: string | null,
  memberCount: number
): boolean[] => {
  const { projectId } = useParams<{ projectId: string }>();
  const results = useQueries({
    queries: Array.from({ length: Math.max(0, memberCount) }, (_, m) => ({
      queryKey: memberArraysQueryKey(projectId, simulationId, m),
      queryFn: () => fetchMemberArrays(projectId!, simulationId!, m),
      enabled: false,
      staleTime: Infinity,
    })),
  });
  return useMemo(
    () => results.map((r) => !!simulationId && r.data !== undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [simulationId, ...results.map((r) => r.data !== undefined)]
  );
};

/**
 * Background-prefetch the given ensemble members' arrays, one at a time and in
 * the order given (nearest slider positions first), so scrubbing onto them
 * renders from cache. Callers pass a window around the slider rather than
 * every member: on a large ensemble, warming them all meant hundreds of
 * requests (each a server-side build the first time) and every member held in
 * browser memory. Already-cached members are skipped without a request.
 */
export const usePrefetchEnsembleMembers = (
  simulationId: string | null,
  members: number[],
  enabled: boolean
) => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const membersKey = members.join(",");
  useEffect(() => {
    if (!enabled || !projectId || !simulationId || !membersKey) return;
    let cancelled = false;
    (async () => {
      for (const m of membersKey.split(",").map(Number)) {
        if (cancelled) return;
        try {
          await queryClient.prefetchQuery({
            queryKey: memberArraysQueryKey(projectId, simulationId, m),
            queryFn: () => fetchMemberArrays(projectId, simulationId, m),
            staleTime: Infinity,
          });
        } catch {
          // Best-effort: a failed member will be fetched on demand instead.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, projectId, simulationId, membersKey, queryClient]);
};

/**
 * Across-member burn-probability stack for the Confidence view. With
 * `pointIdx` set, the stack is scoped to that base ignition point (each
 * member's instance of it) instead of the whole scenario.
 */
export const useFireSimulationConfidence = (
  simulationId: string | null,
  enabled: boolean = true,
  pointIdx: number | null = null
) => {
  const { projectId } = useParams<{ projectId: string }>();
  return useQuery({
    queryKey: ["fireConfidence", projectId, simulationId, pointIdx],
    queryFn: async ({ signal }): Promise<ConfidenceArrays> => {
      const pointParam = pointIdx !== null ? `&point=${pointIdx}` : "";
      return confidenceArraysFromPack(
        await fetchFramePack(
          `/api/projects/${projectId}/fire-simulation-ensemble-confidence?simulation_id=${simulationId}${pointParam}`,
          signal
        )
      );
    },
    enabled: !!projectId && !!simulationId && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Selecting/deselecting an ignition swaps between the whole-scenario and
    // point-scoped stacks; keep the previous one rendered while loading.
    placeholderData: keepPreviousData,
  });
};

/** Point KPIs for the slider-selected ensemble member (combines its partitions). */
export const useFireSimulationMemberPointQuery = (
  simulationId: string | null,
  memberIdx: number | null,
  lat: number | null,
  lng: number | null,
  frame: number | null
) => {
  const { projectId } = useParams<{ projectId: string }>();
  return useQuery({
    queryKey: ["fireMemberPointQuery", projectId, simulationId, memberIdx, lat, lng, frame],
    queryFn: () =>
      api.request<ApiResponse<{
        timestamp: string | null;
        fire_size_ha: number | null;
        assets_impacted: number | null;
        fireline_intensity_kw_m: number | null;
        flame_height_m: number | null;
        rate_of_spread_km_h: number | null;
        arrival_time_hours: number | null;
        message?: string;
      }>>(
        `/api/projects/${projectId}/fire-simulation-member-point-query?simulation_id=${simulationId}&member=${memberIdx}&lat=${lat}&lng=${lng}&frame=${frame}`
      ),
    enabled:
      !!projectId && !!simulationId && memberIdx !== null && memberIdx >= 0 &&
      lat !== null && lng !== null && frame !== null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

/** One base ignition point's final footprint from inspect-footprints. */
export interface InspectFootprintEntry {
  point: number;
  /** FeatureCollection of the point's final fire footprint; null = unknown. */
  footprint: {
    type: "FeatureCollection";
    features: { geometry: { type: string; coordinates: unknown } }[];
  } | null;
}

export interface InspectFootprintsData {
  mode: "member" | "confidence";
  points_per_member: number;
  members: number;
  footprints: InspectFootprintEntry[];
}

/**
 * Per-base-point final fire footprints for ensemble Inspection, fetched ONCE
 * per (simulation, view) — map clicks then hit-test locally and instantly.
 * `memberIdx` null = across all members (confidence); an index scopes to that
 * scenario. Ensembles only — single runs carry per-ignition footprints in
 * their collated results.
 */
export const useFireSimulationInspectFootprints = (
  simulationId: string | null,
  memberIdx: number | null,
  enabled = true
) => {
  const { projectId } = useParams<{ projectId: string }>();
  return useQuery({
    queryKey: ["fireInspectFootprints", projectId, simulationId, memberIdx],
    queryFn: () =>
      api.request<ApiResponse<InspectFootprintsData>>(
        `/api/projects/${projectId}/fire-simulation-inspect-footprints` +
          `?simulation_id=${simulationId}` +
          (memberIdx !== null ? `&member=${memberIdx}` : "")
      ),
    enabled: !!projectId && !!simulationId && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};
